import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/Card";
import { Button } from "./ui/Button";
import { Input } from "./ui/Input";
import { Edit, Trash2, Plus, Loader2, Save, Search } from "lucide-react";
import { blogService, Category } from "../services/blog";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/Dialog";
import { Label } from "./ui/Label";
import { Textarea } from "./ui/Textarea";

export function BlogCategorias() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    meta_title: "",
    meta_description: ""
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await blogService.getCategories();
      setCategories(data);
    } catch (error) {
      toast.error("Erro ao carregar categorias");
    } finally {
      setLoading(false);
    }
  };

  const filteredCategories = categories.filter(cat =>
    cat.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpenDialog = (category: Category | null = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        description: category.description || "",
        meta_title: category.meta_title || "",
        meta_description: category.meta_description || ""
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: "",
        description: "",
        meta_title: "",
        meta_description: ""
      });
    }
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("O nome da categoria é obrigatório");
      return;
    }

    try {
      setSaving(true);
      if (editingCategory) {
        await blogService.updateCategory(editingCategory.id, formData);
        toast.success("Categoria atualizada com sucesso");
      } else {
        await blogService.createCategory(formData);
        toast.success("Categoria criada com sucesso");
      }
      setIsDialogOpen(false);
      loadCategories();
    } catch (error) {
      toast.error("Erro ao salvar categoria");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Tem certeza que deseja excluir esta categoria? Isso pode afetar os posts vinculados.")) return;

    try {
      await blogService.deleteCategory(id);
      toast.success("Categoria removida com sucesso");
      loadCategories();
    } catch (error) {
      toast.error("Erro ao remover categoria");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-xl font-semibold text-[#68686A]">Categorias do Blog</h2>
        <div className="flex gap-4 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Buscar categoria..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 border-gray-300"
            />
          </div>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-[#D4C070] hover:bg-[#849D70] text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nova
          </Button>
        </div>
      </div>

      {/* Versão Desktop - Grid de Cards */}
      <div className="hidden md:grid md:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-3 flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#D4C070]" />
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="col-span-3 py-12 text-center text-gray-500">
            Nenhuma categoria encontrada.
          </div>
        ) : (
          filteredCategories.map((category) => (
            <Card key={category.id} className="border border-gray-200 hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-gray-900">{category.name}</CardTitle>
                <p className="text-xs text-gray-500 font-mono">{category.slug}</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-500 line-clamp-2 min-h-[2.5rem]">
                  {category.description || "Sem descrição"}
                </p>
                <div className="flex gap-2 pt-2 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070] border-gray-300"
                    onClick={() => handleOpenDialog(category)}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Editar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:bg-red-50 hover:border-red-300 border-gray-300"
                    onClick={() => handleDelete(category.id)}
                    disabled={category.id === 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Versão Mobile - Cards Empilhados */}
      <div className="md:hidden space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#D4C070]" />
          </div>
        ) : filteredCategories.length === 0 ? (
          <p className="py-12 text-center text-gray-500">
            Nenhuma categoria encontrada.
          </p>
        ) : (
          filteredCategories.map((category) => (
            <Card key={category.id} className="border border-gray-200">
              <CardContent className="p-4 space-y-3">
                {/* Nome e Slug */}
                <div className="space-y-1">
                  <h3 className="font-semibold text-lg text-gray-900">{category.name}</h3>
                  <p className="text-xs text-gray-500 font-mono">{category.slug}</p>
                </div>

                {/* Descrição */}
                <p className="text-sm text-gray-500">
                  {category.description || "Sem descrição"}
                </p>

                {/* Botões de Ação */}
                <div className="flex gap-2 pt-2 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070] border-gray-300"
                    onClick={() => handleOpenDialog(category)}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Editar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:bg-red-50 hover:border-red-300 border-gray-300"
                    onClick={() => handleDelete(category.id)}
                    disabled={category.id === 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{editingCategory ? "Editar Categoria" : "Nova Categoria"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Nome</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Sustentabilidade"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Breve descrição da categoria..."
                rows={3}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="meta_title">Meta Title (SEO)</Label>
              <Input
                id="meta_title"
                value={formData.meta_title}
                onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                placeholder="Título para mecanismos de busca..."
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="meta_description">Meta Description (SEO)</Label>
              <Textarea
                id="meta_description"
                value={formData.meta_description}
                onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                placeholder="Descrição para mecanismos de busca..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#D4C070] hover:bg-[#849D70] text-white"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
